/**
 * Crypto Worker Manager
 *
 * Manages Web Worker instances for Argon2id key derivation.
 * Provides a Promise-based API with progress callbacks.
 */

type ProgressCallback = (progress: number) => void;

interface DeriveKeyRequest {
  passphrase: Uint8Array;
  salt: Uint8Array;
  params: {
    memory: number;
    iterations: number;
    parallelism: number;
    keyLength: number;
  };
  onProgress?: ProgressCallback;
}

interface PendingRequest {
  resolve: (key: Uint8Array) => void;
  reject: (error: Error) => void;
  onProgress?: ProgressCallback;
}

/**
 * Singleton manager for crypto worker
 */
class CryptoWorkerManager {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, PendingRequest>();
  private nextId = 0;

  /**
   * Initialize the worker
   */
  private async initWorker(): Promise<Worker> {
    if (this.worker) {
      return this.worker;
    }

    // Create worker
    this.worker = new Worker(
      new URL('../workers/crypto.worker.ts', import.meta.url),
      { type: 'module' }
    );

    // Setup message handler with origin validation
    this.worker.addEventListener('message', (e: MessageEvent) => {
      // Security: Validate message structure to prevent injection
      const message = e.data;

      // Validate message has expected structure
      if (!message || typeof message !== 'object' || typeof message.type !== 'string') {
        return; // Ignore malformed messages
      }

      // Only accept known message types
      const allowedTypes = ['ready', 'progress', 'result', 'error'];
      if (!allowedTypes.includes(message.type)) {
        return; // Ignore unknown message types
      }

      if (message.type === 'ready') {
        return;
      }

      // Validate message ID format
      if (typeof message.id !== 'string' || !message.id.startsWith('req-')) {
        return; // Ignore messages with invalid IDs
      }

      const request = this.pendingRequests.get(message.id);
      if (!request) {
        return; // Ignore messages for unknown requests
      }

      if (message.type === 'progress') {
        request.onProgress?.(message.value);
      } else if (message.type === 'result') {
        this.pendingRequests.delete(message.id);
        request.resolve(message.key);
      } else if (message.type === 'error') {
        this.pendingRequests.delete(message.id);
        request.reject(new Error(message.error));
      }
    });

    // Setup error handler (production-safe, no console.error)
    this.worker.addEventListener('error', (error) => {
      // Reject all pending requests
      for (const [id, request] of this.pendingRequests.entries()) {
        request.reject(new Error('Worker error: ' + error.message));
        this.pendingRequests.delete(id);
      }
    });

    return this.worker;
  }

  /**
   * Derive key using Argon2id in Web Worker
   */
  async deriveKey(request: DeriveKeyRequest): Promise<Uint8Array> {
    const worker = await this.initWorker();
    const id = `req-${this.nextId++}`;

    return new Promise<Uint8Array>((resolve, reject) => {
      this.pendingRequests.set(id, {
        resolve,
        reject,
        onProgress: request.onProgress,
      });

      worker.postMessage({
        type: 'deriveKey',
        id,
        passphrase: request.passphrase,
        salt: request.salt,
        params: request.params,
      });
    });
  }

  /**
   * Terminate worker and cleanup
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingRequests.clear();
  }
}

// Singleton instance
const workerManager = new CryptoWorkerManager();

/**
 * Derive encryption key from passphrase using Argon2id (Web Worker)
 *
 * @param passphrase - User passphrase as Uint8Array
 * @param salt - Random salt
 * @param params - Argon2id parameters
 * @param onProgress - Optional progress callback (0-100)
 * @returns Derived key
 */
export async function deriveKeyWithWorker(
  passphrase: Uint8Array,
  salt: Uint8Array,
  params: {
    memory: number;
    iterations: number;
    parallelism: number;
    keyLength: number;
  },
  onProgress?: ProgressCallback
): Promise<Uint8Array> {
  return workerManager.deriveKey({
    passphrase,
    salt,
    params,
    onProgress,
  });
}

/**
 * Check if Web Workers are supported
 */
export function isWorkerSupported(): boolean {
  return typeof Worker !== 'undefined';
}

/**
 * Terminate the crypto worker (for cleanup)
 */
export function terminateCryptoWorker() {
  workerManager.terminate();
}
